import { CircularProgress } from "@material-ui/core";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useMonthSalaryStore } from "web-apps/company/stores/monthSalaryStore/monthSalaryStore";
import { useCompanyFilterOptions } from "../common/hooks/useCompanyFilterOptions";
import { StaffProfilePanel } from "../staff/components/profilePanel/profilePanel.component";
import {
  LoadMoreSentinel,
  LoadMoreSpinner,
  MonthSalaryBody,
} from "./salary.styled";
import { SalaryListRow } from "./salaryListRow.component";
import {
  ColumnListWrapper,
  EmptyState,
} from "../applications/applicationList.styled";

type Props = {
  selectedWorker?: number;
};

const PAGE_SIZE = 25;

export const SalaryList: React.FC<Props> = ({ selectedWorker }) => {
  const { t } = useTranslation();
  const [{ company, companyWorkerImages }] = useCompanyStore();
  const [state, monthSalaryActions] = useMonthSalaryStore();

  // Switching company or picking a worker invalidates the accumulated pages.
  const listKey = `${company?.id ?? "none"}-${selectedWorker ?? "all"}`;
  const [pageState, setPageState] = useState({ listKey, page: 0 });
  const [selectedSalaryWorkerId, setSelectedSalaryWorkerId] = useState<
    number | null
  >(null);

  const filterOptions = useCompanyFilterOptions({ companyId: company?.id });

  // Fall back to the first page until the page state catches up with the key.
  const page = pageState.listKey === listKey ? pageState.page : 0;

  useEffect(() => {
    if (!company?.id) return;
    monthSalaryActions.getAllSalaries(
      company.id,
      page,
      PAGE_SIZE,
      selectedWorker != null ? [selectedWorker] : undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, page, selectedWorker]);

  const displayedWorkers = state.workersWithSalary;

  const hasMore = state.workersWithSalary.length < state.total;

  const handleLoadMore = useCallback((): void => {
    if (!company?.id || state.isLoading || !hasMore) return;
    setPageState({ listKey, page: page + 1 });
  }, [company?.id, hasMore, listKey, page, state.isLoading]);

  const { loadMoreRef } = useInfiniteScroll({
    hasMore,
    isLoading: state.isLoading,
    itemCount: state.workersWithSalary.length,
    onLoadMore: handleLoadMore,
  });

  const handleSelectWorker = (workerId: number): void => {
    setSelectedSalaryWorkerId(workerId);
  };

  const handleCloseSalaryPanel = (): void => {
    setSelectedSalaryWorkerId(null);
  };

  const handleSalaryPanelSaved = (): void => {
    if (!company?.id || selectedSalaryWorkerId == null) return;
    // Only the edited worker's row can have changed, so the accumulated pages
    // stay as they are.
    monthSalaryActions.getWorkerSalary(company.id, selectedSalaryWorkerId);
  };

  const renderSalaries = (): JSX.Element | null => {
    if (displayedWorkers.length === 0) {
      if (state.isLoading) return null;
      return <EmptyState typography="b1">{t("Salary.NoSalaries")}</EmptyState>;
    }

    return (
      <MonthSalaryBody>
        {displayedWorkers.map((salary) => (
          <SalaryListRow
            key={`worker-${salary.workerId}`}
            salary={salary}
            avatarUrl={
              salary.externalId
                ? companyWorkerImages[salary.externalId]
                : undefined
            }
            onSelectWorker={handleSelectWorker}
          />
        ))}
      </MonthSalaryBody>
    );
  };

  return (
    <>
      <ColumnListWrapper>
        {renderSalaries()}
        {state.isLoading && (
          <LoadMoreSpinner>
            <CircularProgress size={24} />
          </LoadMoreSpinner>
        )}
        {hasMore && <LoadMoreSentinel ref={loadMoreRef} />}
      </ColumnListWrapper>

      <StaffProfilePanel
        companyId={company?.id ?? null}
        workerId={selectedSalaryWorkerId}
        unitTeamOptions={filterOptions.unitTeamOptions}
        isOpen={selectedSalaryWorkerId != null}
        initialTab="employmentSalary"
        onClose={handleCloseSalaryPanel}
        onSaved={handleSalaryPanelSaved}
      />
    </>
  );
};
